/* ==================================================================
   Pāw-cipes Feeding Calculator — client-side engine + UI
   100% deterministic. No network for the math (only a one-time fetch
   of the static product catalog asset). No PII in any event.
   Logic follows the build brief Part A (A3–A11).
   ================================================================== */
(function () {
  'use strict';

  /* ----------------------------- math ----------------------------- */
  var round5 = function (n) { return Math.round(n / 5) * 5; };
  var round25 = function (n) { return Math.round(n * 4) / 4; };
  var rer = function (kg) { return 70 * Math.pow(kg, 0.75); };

  var GROWTH_JUNIOR = 2.0;

  // Resolve the maintenance/life-stage factor (before weight-goal overrides).
  function resolveBaseFactor(inp) {
    var sp = inp.species;
    var stage = inp.life_stage;

    if (stage === 'pregnant') { return { factor: 2.0, kind: 'pregnant' }; }
    if (stage === 'young') {
      if (sp === 'cat') { return { factor: 2.5, kind: 'growth' }; }
      if (inp.breed_size === 'large') { return { factor: 2.25, kind: 'growth_large' }; }
      return { factor: 3.0, kind: 'growth' };
    }
    if (stage === 'junior') { return { factor: GROWTH_JUNIOR, kind: 'growth' }; }

    var base;
    if (sp === 'cat') {
      if (stage === 'senior') { base = 1.1; }
      else if (inp.activity === 'low' || inp.activity === 'indoor') { base = 1.0; }
      else if (inp.activity === 'high') { base = 1.5; }
      else { base = inp.neutered ? 1.2 : 1.4; }
    } else {
      if (stage === 'senior') { base = 1.4; }
      else if (inp.activity === 'low' || inp.activity === 'indoor') { base = 1.4; }
      else if (inp.activity === 'high') { base = 2.0; }
      else { base = inp.neutered ? 1.6 : 1.8; }
    }
    return { factor: base, kind: stage === 'senior' ? 'senior' : 'adult' };
  }

  // Feed-to-ideal-weight: BCS + optional target weight drive the calc (A6 ⚑).
  function resolveWeightPlan(inp, base) {
    var stage = inp.life_stage;
    // Growth & pregnancy take priority — never auto-restrict these.
    if (stage === 'young' || stage === 'junior' || stage === 'pregnant') {
      return { weightKg: inp.weight_kg, factor: base.factor, kind: base.kind };
    }
    var wantLose = inp.goal === 'lose' || inp.body_shape === 'chunky';
    var wantGain = inp.goal === 'gain' || inp.body_shape === 'thin';
    var hasTarget = typeof inp.target_weight_kg === 'number' && inp.target_weight_kg > 0;

    if (wantLose && !wantGain) {
      if (hasTarget) { return { weightKg: inp.target_weight_kg, factor: base.factor, kind: 'weight_loss_target' }; }
      return { weightKg: inp.weight_kg, factor: inp.species === 'cat' ? 0.8 : 1.0, kind: 'weight_loss' };
    }
    if (wantGain && !wantLose) {
      if (hasTarget) { return { weightKg: inp.target_weight_kg, factor: base.factor, kind: 'weight_gain_target' }; }
      return { weightKg: inp.weight_kg, factor: inp.species === 'cat' ? 1.4 : 1.8, kind: 'weight_gain' };
    }
    return { weightKg: inp.weight_kg, factor: base.factor, kind: base.kind };
  }

  function mealsForStage(stage) {
    return (stage === 'young' || stage === 'junior' || stage === 'pregnant' || stage === 'nursing') ? 3 : 2;
  }

  function resolveMode(product, completeToggle) {
    switch (product.calculator_role) {
      case 'complete_food': return 'main_meal';
      case 'complete_food_optional': return completeToggle ? 'main_meal_optional' : 'topper';
      case 'topper': return 'topper';
      case 'treat_topper': return 'treat';
      case 'supplement': return 'supplement';
      default: return 'topper';
    }
  }

  function fmtSachets(n) {
    var w = Math.floor(n), half = (n - w) >= 0.5;
    return ((w > 0 ? String(w) : '') + (half ? '½' : '')) || '0';
  }
  function doseLabel(n) { return fmtSachets(n) + ' ' + (n > 1 ? 'sachets' : 'sachet') + '/day'; }
  function rangeLabel(low, high) {
    return (low === high) ? doseLabel(low) : fmtSachets(low) + '–' + fmtSachets(high) + ' sachets/day';
  }

  // Precise dose: keep the old weight-scaling logic, but bound it to the new recommended
  // range — interpolate continuously across the band by body weight (cats are flat).
  function resolveSupplementDose(dosage, species, weightKg) {
    if (!dosage) { return null; }
    if (Array.isArray(dosage)) {                 // legacy weight-only bands
      var lb = dosage[dosage.length - 1];
      for (var i = 0; i < dosage.length; i++) { if (weightKg <= dosage[i].max_kg) { lb = dosage[i]; break; } }
      return { sachets: lb.sticks, low: lb.sticks, high: lb.sticks, label: lb.label, range_label: lb.label, start_low: '' };
    }
    var bands = (species === 'cat') ? dosage.cat : dosage.dog;
    var step = 0.5;                               // ½-step accuracy for cats & dogs; the min-1 floor below prevents a ½ standing dose
    if (!bands || !bands.length) { return null; }
    var b = bands[bands.length - 1];
    for (var j = 0; j < bands.length; j++) { if (weightKg <= bands[j].max_kg) { b = bands[j]; break; } }
    var topKg = b.high_kg || b.max_kg;            // cap interpolation for the open top band
    var span = topKg - b.min_kg;
    var frac = span > 0 ? (weightKg - b.min_kg) / span : 0;
    frac = Math.max(0, Math.min(1, frac));
    var precise = Math.round((b.low + frac * (b.high - b.low)) / step) * step;  // scale by weight to the step
    precise = Math.max(b.low, Math.min(b.high, precise));
    if (precise < 1) { precise = 1; }             // always start from at least 1
    return {
      sachets: precise, low: b.low, high: b.high,
      label: doseLabel(precise), range_label: rangeLabel(b.low, b.high),
      start_low: (species === 'cat' && dosage.sensitive_start) ? dosage.sensitive_start : ''
    };
  }

  function buildFlags(inp, wp) {
    var flags = [];
    if (inp.species === 'cat' && inp.life_stage === 'senior') { flags.push('senior_cat'); }
    if (inp.species === 'dog' && inp.life_stage === 'young' && inp.breed_size === 'large') { flags.push('large_breed_puppy'); }
    if (wp.kind === 'weight_loss' || wp.kind === 'weight_loss_target') { flags.push('weight_loss'); }
    if (wp.kind === 'weight_gain' || wp.kind === 'weight_gain_target') { flags.push('weight_gain'); }
    return flags;
  }

  /* ---------------------- the pure engine ------------------------- */
  // computePlan(inputs, product, completeToggle, meta) -> result
  function computePlan(inp, product, completeToggle, meta) {
    var stage = inp.life_stage;
    var meals = mealsForStage(stage);

    // Nursing / lactating → guidance card, never a single number (A6 ⚑).
    if (stage === 'nursing') {
      return { mode: 'guidance', reason: 'nursing', meals: meals, product: product, flags: [] };
    }

    var mode = resolveMode(product, completeToggle);
    var base = resolveBaseFactor(inp);
    var wp = resolveWeightPlan(inp, base);
    var RER = rer(wp.weightKg);
    var MER = RER * wp.factor;
    var isRich = !!product.rich || product.feeding_rule === 'treat_cap_lower_5_percent_mer';

    var res = {
      mode: mode,
      meals: meals,
      product: product,
      RER: Math.round(RER),
      MER: Math.round(MER),
      factor: wp.factor,
      weightUsed: wp.weightKg,
      weightKind: wp.kind,
      isRich: isRich,
      treatCapKcal: Math.round(MER * (isRich ? 0.05 : 0.10)),
      flags: buildFlags(inp, wp)
    };

    if (mode === 'main_meal' || mode === 'main_meal_optional') {
      var grams = round5(MER * 1000 / product.kcal_per_kg);
      res.grams_per_day = grams;
      res.unit_type = product.unit_type;
      if (product.unit_type === 'can' || product.unit_type === 'stick') {
        res.units_per_day = round25(grams / product.unit_g);
      } else if (product.unit_type === 'gram') {
        res.scoops_per_day = round25(grams / (meta.scoop_g_default || 100));
      }
      res.optional_complete = (mode === 'main_meal_optional');
    } else if (mode === 'topper' || mode === 'treat') {
      var lowPct = 0.05;                      // low end of the band is always 5%
      var highPct = isRich ? 0.05 : 0.10;     // rich items capped at 5%
      var kcalPerG = product.kcal_per_unit / product.unit_g;
      res.allowance = {
        low_kcal: Math.round(MER * lowPct),
        high_kcal: Math.round(MER * highPct),
        low_g: Math.max(1, Math.round(MER * lowPct / kcalPerG)),
        high_g: Math.max(1, Math.round(MER * highPct / kcalPerG)),
        is_rich: isRich,
        start_low: product.feeding_rule === 'topper_percent_mer_with_start_low'
      };
      res.reduce_main_kcal = res.allowance.high_kcal;
    } else if (mode === 'supplement') {
      var dose = resolveSupplementDose(product.dosage, inp.species, inp.weight_kg);
      var sachets = dose ? dose.sachets : 1;
      var kcalAdded = Math.round(sachets * product.kcal_per_unit);
      res.supplement = {
        sticks: sachets,
        label: dose ? dose.label : (sachets + ' sachet/day'),
        range_label: dose ? dose.range_label : '',
        low: dose ? dose.low : sachets,
        high: dose ? dose.high : sachets,
        start_low: (dose && dose.start_low) ? dose.start_low : '',
        dose_note: product.dose_note || '',
        kcal_added: kcalAdded,
        reduce_main_kcal: kcalAdded,
        positioning: product.positioning || '',
        actives: product.active_ingredients || ''
      };
    }
    return res;
  }

  // Expose the pure engine for testing / reuse.
  window.PawFeedingEngine = { computePlan: computePlan, rer: rer, resolveBaseFactor: resolveBaseFactor };

  /* ----------------------- presentation --------------------------- */
  var GOAL_TOKENS = {
    digestion: ['digestion', 'sensitive_stomach', 'sensitive', 'stool'],
    hairball: ['hairball'],
    skin_coat: ['skin', 'coat', 'omega3'],
    joints: ['joints', 'mobility', 'senior'],
    urinary: ['renal', 'urinary'],
    dental: ['dental', 'breath', 'oral'],
    picky: ['picky', 'energy'],
    weight: ['weight_management', 'lean_protein', 'low_fat']
  };

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function roleTag(role) {
    if (role === 'supplement') { return 'Supplement'; }
    if (role === 'topper') { return 'Topper'; }
    if (role === 'treat_topper') { return 'Treat / topper'; }
    if (role === 'complete_food' || role === 'complete_food_optional') { return 'Complete food'; }
    return '';
  }

  var DISCLAIMER = 'A feeding guide, not medical advice. For diagnosed conditions, follow your vet or nutritionist.';
  var MONITOR = 'Check body shape and weight every 2–4 weeks; adjust ±5–10% if weight changes too fast.';
  var SHARED_BUDGET = 'This is your <strong>total daily treat &amp; topper budget</strong> — split it if you feed more than one extra.';

  /* -------- small view helpers -------- */
  function chip(text) { return '<span class="paw-calc__chip">' + text + '</span>'; }
  function unit(u) { return '<span class="paw-calc__hero-unit">' + u + '</span>'; }

  function hero(eyebrow, main, sub, opts) {
    opts = opts || {};
    var cls = 'paw-calc__hero' + (opts.text ? ' paw-calc__hero--text' : '');
    return '<div class="' + cls + '">' +
      (eyebrow ? '<span class="paw-calc__hero-eyebrow">' + eyebrow + '</span>' : '') +
      '<div class="paw-calc__hero-num heading-font">' + main + '</div>' +
      (sub ? '<p class="paw-calc__hero-sub">' + sub + '</p>' : '') +
      '</div>';
  }

  function stat(value, label) {
    return '<div class="paw-calc__stat"><span class="paw-calc__stat-value">' + value +
      '</span><span class="paw-calc__stat-label">' + label + '</span></div>';
  }
  function stats(arr) { return '<div class="paw-calc__stats">' + arr.join('') + '</div>'; }

  function noteIco(kind) {
    if (kind === 'warn') {
      return '<svg class="paw-calc__note-ico" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<path d="M8 1.8l6.4 11.4H1.6L8 1.8z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<path d="M8 6.1v3.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
        '<circle cx="8" cy="11.3" r="0.55" fill="currentColor"/></svg>';
    }
    if (kind === 'accent') {
      return '<svg class="paw-calc__note-ico" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
        '<path d="M8 7.3v3.3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
        '<circle cx="8" cy="4.9" r="0.7" fill="currentColor"/></svg>';
    }
    return '';
  }

  function note(html, kind) {
    var cls = 'paw-calc__note';
    if (kind === 'accent') { cls += ' paw-calc__note--accent'; }
    else if (kind === 'warn') { cls += ' paw-calc__note--warn'; }
    return '<div class="' + cls + '">' + noteIco(kind) + '<div class="paw-calc__note-body">' + html + '</div></div>';
  }

  function meta(html) { return '<p class="paw-calc__meta">' + html + '</p>'; }

  function flagNotes(flags) {
    var html = '';
    if (flags.indexOf('senior_cat') > -1) {
      html += note('Senior cat: we do <strong>not</strong> auto-restrict calories. If an older cat is losing weight, feed more and see your vet.', 'warn');
    }
    if (flags.indexOf('large_breed_puppy') > -1) {
      html += note('Large/giant-breed puppy: aim for <strong>lean, controlled growth</strong>. Over-feeding raises orthopedic risk — keep them lean and re-check often.', 'warn');
    }
    if (flags.indexOf('weight_loss') > -1) {
      html += note('Weight-loss pace: target a slow loss and re-weigh every 2 weeks. Don’t drop below your vet’s safe minimum.', 'warn');
    }
    return html;
  }

  function assumptionsLine(inp) {
    var stageLabel = {
      adult: 'adult', young: inp.species === 'cat' ? 'kitten' : 'puppy', junior: 'junior',
      senior: 'senior', pregnant: 'pregnant', nursing: 'nursing'
    }[inp.life_stage] || inp.life_stage;
    var parts = [stageLabel, inp.neutered ? 'neutered' : 'intact', inp.activity + ' activity',
      'goal: ' + (inp.goal === 'lose' ? 'lose weight' : inp.goal === 'gain' ? 'gain weight' : 'maintain')];
    if (inp.body_shape && inp.body_shape !== 'ideal') { parts.push('body: ' + inp.body_shape); }
    if (typeof inp.target_weight_kg === 'number' && inp.target_weight_kg > 0) { parts.push('target ' + inp.target_weight_kg + ' kg'); }
    return 'Based on: ' + parts.join(' &middot; ') + '.';
  }

  /* -------- per-mode result renderers -------- */
  function renderMainMeal(r) {
    var p = r.product;
    var unitWord = r.unit_type === 'can' ? (r.units_per_day === 1 ? 'can' : 'cans')
      : r.unit_type === 'stick' ? (r.units_per_day === 1 ? 'stick' : 'sticks') : 'scoops';
    var unitVal = r.unit_type === 'gram' ? r.scoops_per_day : r.units_per_day;

    var html = chip('Main meal');
    html += hero('Feed about', r.grams_per_day + unit('g / day'), 'of ' + esc(p.display_name));
    html += stats([
      stat(unitVal + ' ' + unitWord, 'per day'),
      stat(r.meals, 'meals / day'),
      stat(r.MER + ' kcal', 'daily energy')
    ]);
    if (r.optional_complete) {
      html += note('Nutritionally complete, but as a sole diet that’s ~<strong>' + unitVal + ' ' + unitWord +
        '/day</strong> — impractical for most. Best as a premium wet topper or part of a rotation.', 'warn');
    }
    html += note('Keep treats &amp; toppers under <strong>~' + r.treatCapKcal +
      ' kcal/day</strong> (about 10% of energy). ' + SHARED_BUDGET, 'accent');
    html += flagNotes(r.flags);
    html += meta(MONITOR);
    return html;
  }

  function renderTopperTreat(r) {
    var p = r.product, a = r.allowance, isTreat = (r.mode === 'treat');
    var gramsLabel = a.is_rich ? ('up to ~' + a.high_g + ' g') : ('~' + a.low_g + '–' + a.high_g + ' g');

    var html = chip(isTreat ? 'Treat / topper' : 'Topper');
    html += hero('Keep within', gramsLabel + unit('/ day'),
      'of ' + esc(p.display_name) + (a.is_rich ? ' — a rich item, capped at 5% of daily calories' : ''));
    html += stats([
      stat('~' + a.high_kcal + ' kcal', 'treat allowance'),
      stat(r.MER + ' kcal', 'daily energy')
    ]);
    html += note('Complementary food — <strong>not a complete meal</strong>. If you feed this, <strong>reduce the main meal by ~' +
      r.reduce_main_kcal + ' kcal</strong> (about ' + Math.round(r.reduce_main_kcal / 1.1) + ' g less of a complete wet food). ' +
      SHARED_BUDGET, 'accent');
    if (a.start_low) {
      html += note('Contains psyllium — <strong>start very low</strong> and increase over several days, as it shifts stool water balance.', 'warn');
    }
    if (a.is_rich) {
      html += note('Single-ingredient items aren’t balanced (inverted calcium:phosphorus, no added calcium). Keep them occasional, especially for kittens, puppies and cats.', 'warn');
    }
    html += flagNotes(r.flags);
    return html;
  }

  function renderSupplement(r, inp) {
    var p = r.product, s = r.supplement;
    var html = chip('Wellness supplement');
    html += hero('Suggested', esc(s.label), 'of ' + esc(p.display_name), { text: true });
    if (s.low !== s.high) {
      html += meta('Scaled to your ' + (inp.species === 'cat' ? 'cat' : 'dog') + '’s weight (' + inp.weight_kg + ' kg) within the recommended ' + esc(s.range_label) + '.');
    }
    html += stats([
      stat('+' + s.kcal_added + ' kcal', 'added by sachets'),
      stat('−' + s.reduce_main_kcal + ' kcal', 'reduce main meal')
    ]);
    if (s.start_low) { html += note(esc(s.start_low)); }
    html += note('These sachet calories count toward the ~10% daily treat budget. <strong>' +
      esc(s.dose_note || 'If you exceed the treat limit, adjust the main meal accordingly.') +
      '</strong> (about −' + s.reduce_main_kcal + ' kcal).', 'accent');
    if (s.positioning) { html += note(esc(s.positioning)); }
    if (s.actives) { html += meta('Per sachet: ' + esc(s.actives) + '.'); }
    html += flagNotes(r.flags);
    return html;
  }

  function renderGuidance(r) {
    var html = chip('Vet guidance');
    html += hero('', 'Feed free-choice while nursing', 'A single number would under-feed a nursing mother.', { text: true });
    html += note('During peak lactation, energy needs rise to <strong>2–6× the resting requirement</strong> and depend on litter size. Offer food <strong>free-choice (always available)</strong>, keep fresh water down, and work with your vet to track the mother’s weight and the litter’s growth.', 'accent');
    html += meta('Feed ' + r.meals + '+ meals a day, or simply leave a complete food available at all times.');
    html += meta(MONITOR);
    return html;
  }

  function renderResult(root, r, inp, products) {
    var out = root.querySelector('[data-paw="result"]');
    var empty = root.querySelector('[data-paw="result-empty"]');
    var body = '';
    switch (r.mode) {
      case 'main_meal': case 'main_meal_optional': body = renderMainMeal(r); break;
      case 'topper': case 'treat': body = renderTopperTreat(r); break;
      case 'supplement': body = renderSupplement(r, inp); break;
      case 'guidance': body = renderGuidance(r); break;
      default: body = '';
    }
    if (r.mode !== 'guidance') { body += meta(assumptionsLine(inp)); }
    body += '<p class="paw-calc__disclaimer">' + DISCLAIMER + '</p>';

    out.innerHTML = '<div class="paw-calc__result-card">' + body + '</div>';
    out.hidden = false;
    if (empty) { empty.hidden = true; }

    renderWellness(root, inp, products, r.product);
  }

  // Inputs no longer describe a pet (weight cleared, zero, implausible, or no product):
  // drop the old plan so it can't contradict what's typed.
  function showEmpty(root) {
    ['result', 'wellness-out', 'capture'].forEach(function (key) {
      var el = root.querySelector('[data-paw="' + key + '"]');
      if (el) { el.hidden = true; }
    });
    var empty = root.querySelector('[data-paw="result-empty"]');
    if (empty) { empty.hidden = false; }
  }

  // Stacked layout (<900px) renders the result below the form, out of view. Scroll to it
  // once, only on a user-driven change (never on restore or per-keystroke input renders).
  function revealResult(root) {
    if (!window.matchMedia || !window.matchMedia('(max-width: 899px)').matches) { return false; }
    var out = root.querySelector('[data-paw="result"]');
    if (!out || out.hidden || typeof out.scrollIntoView !== 'function') { return false; }
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 'auto' would inherit the theme's html { scroll-behavior: smooth }
    out.scrollIntoView({ block: 'start', behavior: reduce ? 'instant' : 'smooth' });
    return true;
  }

  function renderWellness(root, inp, products, currentProduct) {
    var wrap = root.querySelector('[data-paw="wellness-out"]');
    if (!wrap) { return; }
    var tokens = {};
    inp.wellness_goals.forEach(function (k) {
      (GOAL_TOKENS[k] || []).forEach(function (t) { tokens[t] = true; });
    });
    if (!inp.wellness_goals.length) { wrap.hidden = true; wrap.innerHTML = ''; return; }

    var recs = products.filter(function (p) {
      return p.calculator_role !== 'bundle_hidden' &&
        (p.species === inp.species || p.species === 'both') &&
        p.id !== currentProduct.id &&
        p.wellness_goals.some(function (g) { return tokens[g]; });
    }).slice(0, 6);

    if (!recs.length) { wrap.hidden = true; wrap.innerHTML = ''; return; }

    var cards = recs.map(function (p) {
      var thumb = p.image
        ? '<img class="paw-calc__rec-thumb" src="' + esc(p.image) + '" alt="" loading="lazy" width="56" height="56">'
        : '';
      var body = '<span class="paw-calc__rec-body">' +
        '<span class="paw-calc__rec-tag">' + roleTag(p.calculator_role) + '</span>' +
        '<span class="paw-calc__rec-name">' + esc(p.display_name) + '</span>' +
        '<span class="paw-calc__rec-pos">' + esc((p.positioning || '').split('. ')[0]) + '.</span>' +
        '</span>';
      if (p.handle) {
        return '<a class="paw-calc__rec" href="/products/' + encodeURIComponent(p.handle) + '">' + thumb + body + '</a>';
      }
      return '<div class="paw-calc__rec">' + thumb + body + '</div>';
    }).join('');

    wrap.innerHTML = '<h3 class="paw-calc__wellness-title heading-font">Wellness picks for your goals</h3>' +
      '<div class="paw-calc__rec-grid">' + cards + '</div>' +
      '<p class="paw-calc__meta">Suggestions only — these do not change the feeding amount above.</p>';
    wrap.hidden = false;
  }

  /* ------------------------- controller --------------------------- */
  function val(root, key) {
    var el = root.querySelector('[data-paw="' + key + '"]');
    return el ? el.value : '';
  }
  function checkedRadio(root, key) {
    var el = root.querySelector('[data-paw="' + key + '"]:checked');
    return el ? el.value : '';
  }

  function readInputs(root) {
    var species = checkedRadio(root, 'species') || 'cat';
    var weight = parseFloat(val(root, 'weight'));
    var target = parseFloat(val(root, 'target_weight'));
    var wellness = [];
    root.querySelectorAll('[data-paw="wellness"]:checked').forEach(function (c) { wellness.push(c.value); });
    return {
      species: species,
      weight_kg: isNaN(weight) ? null : weight,
      life_stage: val(root, 'life_stage') || 'adult',
      neutered: (val(root, 'neutered') || 'yes') === 'yes',
      activity: val(root, 'activity') || 'moderate',
      breed_size: val(root, 'breed_size') || 'small',
      body_shape: val(root, 'body_shape') || 'ideal',
      goal: val(root, 'goal') || 'maintain',
      target_weight_kg: isNaN(target) ? null : target,
      wellness_goals: wellness
    };
  }

  function plausibleWeight(species, kg) {
    if (species === 'cat') { return kg >= 0.2 && kg <= 14; }
    return kg >= 0.5 && kg <= 100;
  }

  function buildProductOptions(select, products, species) {
    var families = {};
    products.forEach(function (p) {
      if (p.calculator_role === 'bundle_hidden') { return; }
      if (!(p.species === species || p.species === 'both')) { return; }
      (families[p.family] = families[p.family] || []).push(p);
    });
    var html = '<option value="" disabled selected>Choose a product</option>';
    Object.keys(families).forEach(function (fam) {
      html += '<optgroup label="' + esc(fam) + '">';
      families[fam].forEach(function (p) {
        html += '<option value="' + esc(p.id) + '">' + esc(p.display_name) + '</option>';
      });
      html += '</optgroup>';
    });
    select.innerHTML = html;
  }

  function relabelLifeStage(root, species) {
    var sel = root.querySelector('[data-paw="life_stage"]');
    if (!sel) { return; }
    var labels = species === 'cat'
      ? { young: 'Kitten (under 7 months)', junior: 'Junior (7–12 months)' }
      : { young: 'Puppy (under 4 months)', junior: 'Junior (4–12 months)' };
    Array.prototype.forEach.call(sel.options, function (o) {
      if (labels[o.value]) { o.textContent = labels[o.value]; }
    });
  }

  function init(root) {
    var dataUrl = root.getAttribute('data-products-url');
    var errEl = root.querySelector('[data-paw="error"]');
    var productSel = root.querySelector('[data-paw="product"]');
    var state = { products: [], meta: {}, byId: {}, revealed: false };

    function recompute(reveal) {
      var inp = readInputs(root);
      toggleBreedSize(root, inp.species);
      var productId = productSel.value;
      var product = state.byId[productId];

      var completeWrap = root.querySelector('[data-paw="complete-toggle"]');
      if (completeWrap) {
        completeWrap.hidden = !(product && product.calculator_role === 'complete_food_optional');
      }

      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
      if (inp.weight_kg === null || inp.weight_kg <= 0 || !product) { showEmpty(root); return; }
      if (!plausibleWeight(inp.species, inp.weight_kg)) {
        showEmpty(root);
        if (errEl) {
          errEl.hidden = false;
          errEl.textContent = 'That weight looks unusual for a ' + inp.species + '. Please double-check (kg).';
        }
        return;
      }
      var completeToggle = root.querySelector('[data-paw="complete-check"]');
      var r = computePlan(inp, product, completeToggle && completeToggle.checked, state.meta);
      renderResult(root, r, inp, state.products);
      if (reveal === true && !state.revealed) { state.revealed = revealResult(root); }
      updateCapture(root, inp, product, r);
      persist(root, inp, productId);
      emitPixel(inp, product, r);
    }

    root.addEventListener('change', function (e) {
      // Only calculator controls; the email-capture form lives inside root too
      if (!e.target.matches('[data-paw]') || e.target.closest('[data-paw="capture"]')) { return; }
      recompute(true);
    });
    root.addEventListener('input', function (e) {
      if (e.target.matches('[data-paw="weight"], [data-paw="target_weight"]')) { recompute(); }
    });

    var advBtn = root.querySelector('[data-paw="advanced-toggle"]');
    var advPanel = root.querySelector('[data-paw="advanced"]');
    if (advBtn && advPanel) {
      advBtn.addEventListener('click', function () {
        var open = advPanel.hidden;
        advPanel.hidden = !open;
        advBtn.setAttribute('aria-expanded', String(open));
      });
    }

    fetch(dataUrl, { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) { throw new Error('HTTP ' + res.status); } return res.json(); })
      .then(function (data) {
        state.products = data.products || [];
        state.meta = data._meta || {};
        state.products.forEach(function (p) { state.byId[p.id] = p; });
        var sp = checkedRadio(root, 'species') || 'cat';
        buildProductOptions(productSel, state.products, sp);
        relabelLifeStage(root, sp);
        restore(root);
        recompute();
        root.querySelectorAll('[data-paw="species"]').forEach(function (radio) {
          radio.addEventListener('change', function () {
            buildProductOptions(productSel, state.products, radio.value);
            relabelLifeStage(root, radio.value);
            var out = root.querySelector('[data-paw="result"]');
            var empty = root.querySelector('[data-paw="result-empty"]');
            if (out) { out.hidden = true; }
            if (empty) { empty.hidden = false; }
          });
        });
      })
      .catch(function (err) {
        if (errEl) {
          errEl.hidden = false;
          errEl.textContent = 'Could not load product data. Please refresh and try again.';
        }
        if (window.console) { console.error('[paw-calc] product load failed', err); }
      });
  }

  function toggleBreedSize(root, species) {
    var row = root.querySelector('[data-paw="breed-size-row"]');
    if (row) { row.hidden = (species !== 'dog'); }
  }

  /* ---- sessionStorage (no third-party storage) ---- */
  var SKEY = 'paw-calc-v1';
  function persist(root, inp, productId) {
    try { sessionStorage.setItem(SKEY, JSON.stringify({ inp: inp, productId: productId })); } catch (e) { /* ignore */ }
  }
  function restore(root) {
    var raw;
    try { raw = sessionStorage.getItem(SKEY); } catch (e) { return; }
    if (!raw) { return; }
    var saved;
    try { saved = JSON.parse(raw); } catch (e) { return; }
    if (!saved || !saved.inp) { return; }
    var i = saved.inp;
    setRadio(root, 'species', i.species);
    setVal(root, 'weight', i.weight_kg);
    setVal(root, 'life_stage', i.life_stage);
    setVal(root, 'neutered', i.neutered ? 'yes' : 'no');
    setVal(root, 'activity', i.activity);
    setVal(root, 'body_shape', i.body_shape);
    setVal(root, 'goal', i.goal);
    setVal(root, 'breed_size', i.breed_size);
    if (i.target_weight_kg) { setVal(root, 'target_weight', i.target_weight_kg); }
    if (saved.productId) {
      var sel = root.querySelector('[data-paw="product"]');
      if (sel && sel.querySelector('option[value="' + (window.CSS && CSS.escape ? CSS.escape(saved.productId) : saved.productId) + '"]')) {
        sel.value = saved.productId;
      }
    }
    (i.wellness_goals || []).forEach(function (g) {
      var c = root.querySelector('[data-paw="wellness"][value="' + g + '"]');
      if (c) { c.checked = true; }
    });
  }
  function setVal(root, key, v) {
    var el = root.querySelector('[data-paw="' + key + '"]');
    if (el && v != null && v !== '') { el.value = v; }
  }
  function setRadio(root, key, v) {
    var el = root.querySelector('[data-paw="' + key + '"][value="' + v + '"]');
    if (el) { el.checked = true; }
  }

  /* ---- email capture (tags feed the replenishment automations) ---- */
  function sanitizeTag(v) {
    return String(v).toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function updateCapture(root, inp, product, r) {
    var card = root.querySelector('[data-paw="capture"]');
    if (!card) { return; }
    card.hidden = false;
    var tagsInput = card.querySelector('input[data-signup-tags]');
    if (!tagsInput) { return; }
    var tags = (tagsInput.getAttribute('data-base-tags') || 'newsletter,feeding-calc').split(',');
    tags.push('feeding-calc-' + sanitizeTag(inp.species));
    if (r && r.grams_per_day > 0) { tags.push('paw-gpd-' + Math.round(r.grams_per_day)); }
    if (product && product.id) { tags.push('paw-prod-' + sanitizeTag(product.id)); }
    tagsInput.value = tags.join(',');
  }

  /* ---- retargeting pixel (consent-gated; no PII) ---- */
  function emitPixel(inp, product, r) {
    if (r.mode === 'guidance') { return; }
    var detail = {
      species: inp.species,
      life_stage: inp.life_stage,
      goal: inp.goal,
      wellness_goals: inp.wellness_goals,
      product_handle: product.id,
      mode: r.mode
    };
    try {
      document.dispatchEvent(new CustomEvent('paw:feeding-calculated', { detail: detail }));
    } catch (e) { /* ignore */ }
    if (window.PawPixels && typeof window.PawPixels.feedingPlanCalculated === 'function') {
      window.PawPixels.feedingPlanCalculated(detail);
    }
  }

  /* ----------------------------- boot ----------------------------- */
  function boot() {
    document.querySelectorAll('.paw-calc[data-products-url]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
